import { Lock, Crown, Cpu, Cloud } from "lucide-react";
import { toolRuntime, NON_FILE_TOOLS, NON_FILE_BADGE } from "@/lib/tool-runtime";
import { useLocale } from "@/hooks/useLocale";
import { useFreemium } from "@/hooks/useFreemium";
import { isEventSuiteTool } from "@/lib/workflow/suites";
import { trpc } from "@/lib/trpc";
import { useAuth } from "@/_core/hooks/useAuth";

/**
 * Trust pill(s) + Pro/Admin status in the tool page title row.
 *
 * Used to also carry a "10 free uses daily" pill and a redundant
 * "X free uses left today" pill here -- between those two and
 * UsageAndUpgradeStrip (which shows directly below this, with real
 * Quick Buy buttons attached), the same free-usage count was being
 * stated three separate times right next to the title. Cut down to
 * one: UsageAndUpgradeStrip is now the single place that states it,
 * since it's also the only one of the three that does anything
 * (upgrade buttons) rather than just repeat the number. What stays
 * here is genuinely different information -- the file-privacy trust
 * signal, and Pro/Admin status for the one case UsageAndUpgradeStrip
 * itself goes silent (it returns null once someone's already Pro/
 * admin, so without this they'd have no status indicator here at all).
 */
export function ToolTitleBadges({ toolId }: { toolId?: string }) {
  const { strings } = useLocale();
  const { isPro, isAdmin, isLoading } = useFreemium();
  const runtime = toolId ? toolRuntime(toolId) : null;
  // Event Suite Premium can save projects to the account, so for those subscribers "never stored" would be wrong.
  const { isAuthenticated } = useAuth();
  const suiteStatus = trpc.eventSuite.status.useQuery(undefined, { enabled: isAuthenticated && isEventSuiteTool(toolId), staleTime: 60_000 });
  const savesToAccount = isEventSuiteTool(toolId) && !!suiteStatus.data?.premium;

  return (
    <div className="flex flex-wrap items-center gap-2 mt-2">
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-primary/10 text-primary border border-primary/20">
        <Lock className="h-3 w-3" />
        {savesToAccount ? "Saved only when you choose" : (runtime?.privacyBadge ?? (toolId && NON_FILE_TOOLS.has(toolId) ? NON_FILE_BADGE : strings.trustBadges.filesNeverStored))}
      </span>
      {runtime && (
        <span title={runtime.detail} data-testid="runtime-badge" data-kind={runtime.kind} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-muted text-muted-foreground border">
          {runtime.kind === "device" || runtime.kind === "engine" ? <Cpu className="h-3 w-3" /> : <Cloud className="h-3 w-3" />}
          {runtime.label}
        </span>
      )}
      {runtime && runtime.kind !== "device" && <p className="w-full text-xs text-muted-foreground" data-testid="runtime-detail">{runtime.detail}</p>}
      {!isLoading && (isPro || isAdmin) && !isEventSuiteTool(toolId) && (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800 dark:bg-amber-950/50 dark:text-amber-400 border border-amber-200 dark:border-amber-900">
          <Crown className="h-3 w-3" />
          {strings.toolPage.proBadge}
        </span>
      )}
    </div>
  );
}
